import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ClientPassengerLimits({ user, operatorDetails }) {
    const [clients, setClients] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Store original and modified limits
    const [limits, setLimits] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            if (!operatorDetails || !operatorDetails._id) return;
            try {
                // Fetch Subscription to get the total passenger limit
                const subRes = await axios.get(`/api/subscriptions/operator/${operatorDetails._id}`);
                let activeSub = null;
                if (subRes.data && subRes.data.length > 0) {
                    activeSub = subRes.data.find(s => s.status === 'Active') || subRes.data[0];
                    setSubscription(activeSub);
                }

                // Fetch Clients
                const clientsRes = await axios.get(`/api/clients/${operatorDetails._id}`);
                setClients(clientsRes.data);
                
                // Initialize limits map
                const limitsMap = {};
                clientsRes.data.forEach(c => {
                    limitsMap[c._id] = c.passengerLimit || 0;
                });
                setLimits(limitsMap);

            } catch (err) {
                console.error("Failed to fetch data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [operatorDetails]);

    const totalAllowed = subscription?.planId?.passengerLimit || -1;
    const isUnlimited = totalAllowed === -1;

    const currentTotalAllocated = Object.values(limits).reduce((acc, val) => acc + (Number(val) || 0), 0);
    const remainingLimit = isUnlimited ? 'Unlimited' : totalAllowed - currentTotalAllocated;
    const isExceeded = !isUnlimited && currentTotalAllocated > totalAllowed;

    const handleLimitChange = (clientId, value) => {
        const numValue = value === '' ? 0 : Number(value);
        if (numValue < 0) return; // Prevent negative limits
        setLimits(prev => ({ ...prev, [clientId]: numValue }));
    };

    const handleSave = async () => {
        if (isExceeded) {
            alert(`Total allocated limits (${currentTotalAllocated}) cannot exceed your allowed limit (${totalAllowed}).`);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                limits: Object.keys(limits).map(clientId => ({
                    clientId,
                    passengerLimit: limits[clientId]
                }))
            };
            await axios.put('/api/clients/bulk/passenger-limits', payload);
            alert("Passenger limits updated successfully.");
        } catch (err) {
            console.error("Failed to save passenger limits:", err);
            alert("An error occurred while saving.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#6b7280', padding: 40 }}>
                Loading Passenger Limits...
            </div>
        );
    }

    return (
        <div className="op-container" style={{ padding: '32px', maxWidth: '1280px', margin: '0 auto', fontFamily: "'Outfit', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
            <header className="op-header" style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="op-header-left">
                    <h1 className="op-title" style={{ fontSize: '26px', fontWeight: 800, margin: '0 0 6px 0', color: '#0f172a', letterSpacing: '-0.02em' }}>Client Passenger Limits</h1>
                    <p className="op-subtitle" style={{ color: '#64748b', margin: 0, fontSize: '14px', fontWeight: 500 }}>Distribute and manage your operational passenger capacity across registered corporate clients.</p>
                </div>
                <button 
                    onClick={handleSave} 
                    disabled={saving || isExceeded}
                    style={{
                        padding: '12px 28px',
                        background: (saving || isExceeded) ? '#94a3b8' : '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '14px',
                        cursor: (saving || isExceeded) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: (saving || isExceeded) ? 'none' : '0 4px 14px rgba(37, 99, 235, 0.3)'
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </header>

            {/* Modern Metric KPI Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px',
                marginBottom: '28px'
            }}>
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    padding: '22px 24px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '18px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '14px',
                        background: '#eff6ff',
                        color: '#2563eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                        </svg>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '4px' }}>Total Plan Limit</div>
                        <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                            {isUnlimited ? 'Unlimited' : totalAllowed.toLocaleString()}
                        </div>
                    </div>
                </div>

                <div style={{
                    background: isExceeded ? '#fef2f2' : '#ffffff',
                    border: `1px solid ${isExceeded ? '#fca5a5' : '#e2e8f0'}`,
                    borderRadius: '16px',
                    padding: '22px 24px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '18px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '14px',
                        background: isExceeded ? '#fee2e2' : '#eff6ff',
                        color: isExceeded ? '#dc2626' : '#2563eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '4px' }}>Total Allocated</div>
                        <div style={{ fontSize: '28px', fontWeight: 800, color: isExceeded ? '#dc2626' : '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                            {currentTotalAllocated.toLocaleString()}
                        </div>
                    </div>
                </div>

                {!isUnlimited && (
                    <div style={{
                        background: isExceeded ? '#fef2f2' : '#ffffff',
                        border: `1px solid ${isExceeded ? '#fca5a5' : '#e2e8f0'}`,
                        borderRadius: '16px',
                        padding: '22px 24px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '18px'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '14px',
                            background: isExceeded ? '#fee2e2' : '#ecfdf5',
                            color: isExceeded ? '#dc2626' : '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 14 14"></polyline>
                            </svg>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '4px' }}>Remaining Limit</div>
                            <div style={{ fontSize: '28px', fontWeight: 800, color: isExceeded ? '#dc2626' : '#10b981', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                                {typeof remainingLimit === 'number' ? remainingLimit.toLocaleString() : remainingLimit}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {isExceeded && (
                <div style={{
                    padding: '14px 20px',
                    borderRadius: '12px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#b91c1c',
                    fontSize: '13.5px',
                    fontWeight: 600,
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    Total allocated limits exceed your plan capacity ({currentTotalAllocated} / {totalAllowed}). Please reduce allocated numbers before saving.
                </div>
            )}

            {/* Clients Table */}
            <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <tr>
                            <th style={{ padding: '16px 24px', fontSize: '12px', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client Name</th>
                            <th style={{ padding: '16px 24px', fontSize: '12px', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Industry</th>
                            <th style={{ padding: '16px 24px', fontSize: '12px', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Passengers</th>
                            <th style={{ padding: '16px 24px', fontSize: '12px', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Allocated Limit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.length === 0 ? (
                            <tr>
                                <td colSpan="4" style={{ padding: '50px 24px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: 500 }}>
                                    No clients found. Add some clients first.
                                </td>
                            </tr>
                        ) : (
                            clients.map(client => {
                                const currentCount = client.contacts ? client.contacts.length : 0;
                                const isOverAllocated = limits[client._id] && currentCount > limits[client._id];
                                return (
                                    <tr key={client._id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                                        <td style={{ padding: '18px 24px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '10px',
                                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                                    color: '#2563eb',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 800,
                                                    fontSize: '15px',
                                                    border: '1px solid #bfdbfe',
                                                    flexShrink: 0
                                                }}>
                                                    {(client.companyName || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '14.5px' }}>{client.companyName}</div>
                                                    <div style={{ fontSize: '12.5px', color: '#94a3b8', marginTop: '2px' }}>{client.contactPerson || 'No contact assigned'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '18px 24px' }}>
                                            <span style={{
                                                padding: '4px 10px',
                                                borderRadius: '8px',
                                                background: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: '#475569'
                                            }}>
                                                {client.industry || 'General'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '18px 24px' }}>
                                            <span style={{ 
                                                display: 'inline-flex', 
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '5px 12px', 
                                                background: isOverAllocated ? '#fee2e2' : '#f1f5f9', 
                                                borderRadius: '20px', 
                                                fontWeight: 700,
                                                fontSize: '12.5px',
                                                color: isOverAllocated ? '#dc2626' : '#334155'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="9" cy="7" r="4"></circle>
                                                </svg>
                                                {currentCount} Passengers
                                            </span>
                                        </td>
                                        <td style={{ padding: '18px 24px' }}>
                                            <input 
                                                type="number" 
                                                min="0"
                                                value={limits[client._id] || 0} 
                                                onChange={(e) => handleLimitChange(client._id, e.target.value)}
                                                style={{
                                                    width: '110px',
                                                    padding: '9px 14px',
                                                    borderRadius: '10px',
                                                    border: `1px solid ${isOverAllocated ? '#fca5a5' : '#cbd5e1'}`,
                                                    outline: 'none',
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    color: '#0f172a',
                                                    background: '#ffffff',
                                                    transition: 'border 0.2s, box-shadow 0.2s',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                                onBlur={(e) => e.target.style.borderColor = isOverAllocated ? '#fca5a5' : '#cbd5e1'}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
